/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { Alert, Button, classnames, PillButton, Title } from '@terveystalo/design-system-react-components';
import { theme } from '@terveystalo/design-system-theme';
import { ApiModelType } from 'model/api';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ComboboxItem } from 'ui/questionary/ComboboxItem';
import { ProgressBar } from 'ui/questionary/ProgressBar';
import { SearchCombobox } from 'ui/questionary/SearchCombobox';
import { Language } from 'utils/types/common';
import { RemoteData } from 'utils/types/remoteData';

type PopularSymptoms = {
  [key in Language]: string[];
};

type Props = {
  onClickNext: () => void;
  handleSymptomAddition: (symptom: string) => void;
  handleSymptomReduction: (symptom: string) => void;
  keywordsData: RemoteData<ApiModelType['KeywordsResponse']>;
  selectedSymptoms: string[];
};

export const PreForm: FC<Props> = ({
  onClickNext,
  handleSymptomAddition,
  handleSymptomReduction,
  keywordsData,
  selectedSymptoms,
}) => {
  const { t, i18n } = useTranslation();
  const language = i18n.language as Language;
  const maxSelectedSymptoms = selectedSymptoms.length > 3;
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [searchField, setSearchField] = useState<string>('');

  const popularSymptoms: PopularSymptoms = {
    fi: [
      t('popular_symptoms_flu'),
      t('popular_symptoms_coronavirus'),
      t('popular_symptoms_sore_throat'),
      t('popular_symptoms_back_pain'),
      t('popular_symptoms_knee_pain'),
      t('popular_symptoms_shoulder_pain'),
      t('popular_symptoms_neck_pain'),
      t('popular_symptoms_stomach_ache'),
      t('popular_symptoms_stomach_flu'),
      t('popular_symptoms_diarrhea'),
    ],
    sv: [
      t('popular_symptoms_heartburn'),
      t('popular_symptoms_dizziness'),
      t('popular_symptoms_stomach_ache'),
      t('popular_symptoms_headache'),
      t('popular_symptoms_back_pain'),
      t('popular_symptoms_fever'),
    ],
    en: [
      t('popular_symptoms_heartburn'),
      t('popular_symptoms_dizziness'),
      t('popular_symptoms_stomach_ache'),
      t('popular_symptoms_headache'),
      t('popular_symptoms_back_pain'),
      t('popular_symptoms_flu'),
      t('popular_symptoms_sore_throat'),
      t('popular_symptoms_earache'),
      t('popular_symptoms_fever'),
    ],
  };

  const onChange = (v: string) => {
    setSearchField(v);
  };

  /* 
    Logic to filter results to show in searchCombobox. All keyword results from
    backend should be in lower case.
  */
  const filteredResults: string[] =
    keywordsData.status === 'REMOTE_DATA_SUCCESS' && searchField.length > 2
      ? keywordsData.data.keywords
          .filter(t => t.includes(searchField.toLowerCase()))
          .sort((a, b) => {
            return a.indexOf(searchField) < b.indexOf(searchField) ? -1 : 1;
          })
      : [];

  /* 
    Message to show in seachCombobox when no results are shown. This needs to
    be updated if filtering rules are updated. These two rulesets could probably
    be combined later.
  */
  const searchboxMsg =
    keywordsData.status === 'REMOTE_DATA_SUCCESS'
      ? searchField.length < 3
        ? t('search_continue_writing') + '...'
        : filteredResults.length < 1
        ? t('search_no_results')
        : undefined
      : keywordsData.status === 'REMOTE_DATA_LOADING'
      ? t('loading')
      : t('error');
  const valuesToShow: string[] =
    keywordsData.status === 'REMOTE_DATA_SUCCESS' && filteredResults.length > 0 ? filteredResults : [];

  const focusableIds = valuesToShow.map(v => `id-@´${v}`);
  return (
    <div className={classnames('flex', 'flex-col', 'mb-2')}>
      <ProgressBar currentStep={1} />
      <Title as="h1" size="xl" weight="normal">
        {t('what_symptoms_you_have')}
      </Title>
      <p className={classnames('mb-1', 'text-body-l')}>{t('select_symptoms_and_proceed')}</p>
      <div className={classnames('mb-1.5')}>
        <h2 className={classnames('font-subtitle', 'text-subtitle-s')}>{t('most_popular_symptoms')}</h2>
        <div>
          {popularSymptoms[language].map(s => (
            <PillButton
              key={s}
              aria-label={`${t('select_symptom')}: ${s}`}
              className={classnames('mr-0.5', 'mt-0.5')}
              onClick={() => handleSymptomAddition(s)}
              disabled={!!selectedSymptoms.find(e => e === s)}
            >
              {s}
            </PillButton>
          ))}
        </div>
      </div>
      <SearchCombobox
        type="popup"
        id="searchcombo"
        searchValue={searchField}
        isOpen={isPopupOpen}
        setIsOpen={setIsPopupOpen}
        onChange={onChange}
        focusableItemIds={focusableIds}
      >
        {searchboxMsg && <p className={classnames('ml-1', 'text-zodiac-black-65')}>{searchboxMsg}</p>}
        {valuesToShow.map(item => (
          <ComboboxItem
            key={`id-@´${item}`}
            id={`id-@´${item}`}
            text={item}
            onClick={() => {
              handleSymptomAddition(item), setSearchField(''), setIsPopupOpen(false);
            }}
            css={css`
              &.focused {
                box-shadow: inset 0 0 0 ${theme.border.width.xs} ${theme.color.zodiacBlack};
              }
            `}
          />
        ))}
      </SearchCombobox>
      <div className={classnames('mb-2')}>
        <h2 className={classnames('font-subtitle', 'text-subtitle-s', 'mb-0.5', 'block')}>{t('symptoms_you_chose')}</h2>
        {selectedSymptoms.length < 1 ? (
          <p className={classnames('text-body-m')}>{t('no_selected_symptoms')}</p>
        ) : (
          <div className={classnames('p-0.5', 'bg-white')}>
            {selectedSymptoms.map(s => (
              <PillButton
                key={s}
                aria-label={`${t('remove_selected_symptom')}: ${s}`}
                className={classnames('mx-0.5', 'my-0.5')}
                iconRight="CloseSmall"
                onClick={() => handleSymptomReduction(s)}
                grayBackground
              >
                {s}
              </PillButton>
            ))}
          </div>
        )}
      </div>
      {maxSelectedSymptoms && (
        <Alert icon="AlertOctagon" variant="error" role="region" aria-live="polite" className={classnames('mb-0.5')}>
          {t('error_max_selected_symptoms')}
        </Alert>
      )}
      <Button
        id="next-button"
        as="button"
        type="button"
        icon="ArrowRight"
        onClick={onClickNext}
        className={classnames('mb-1')}
        disabled={selectedSymptoms.length < 1 || maxSelectedSymptoms}
      >
        {t('next')}
      </Button>
    </div>
  );
};
